import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";

// ---------------------------------------------------------------------------
// One lesson card on the draft board.
//
// Colour is DERIVED FROM THE SUBJECT, never randomised: the same subject gets
// the same swatch on every render, in every view, in the tray and in the grid —
// that consistency is what makes an aSc-style board scannable. The palette is a
// fixed table (the project's "extend the table, not component ternaries" rule),
// each entry a light-mode-legible 50/300/700 triple: pale fill, mid border, dark
// text, saturated accent bar. Contrast of every text-on-fill pair is well past
// 7:1. The app ships no dark mode (no `darkMode` key in tailwind.config.js, no
// `dark:` utilities anywhere in src/), so a single light scheme is correct here.
//
// The wrapper is a <div> and the click target a nested <button> on purpose: the
// lock toggle is a second button, and a button inside a button is invalid HTML.
// ---------------------------------------------------------------------------

const SUBJECT_PALETTE = [
  { fill: "#eef2ff", border: "#c7d2fe", text: "#3730a3", accent: "#6366f1" }, // indigo
  { fill: "#ecfeff", border: "#a5f3fc", text: "#155e75", accent: "#06b6d4" }, // cyan
  { fill: "#ecfdf5", border: "#a7f3d0", text: "#065f46", accent: "#10b981" }, // emerald
  { fill: "#f7fee7", border: "#d9f99d", text: "#3f6212", accent: "#84cc16" }, // lime
  { fill: "#fffbeb", border: "#fde68a", text: "#92400e", accent: "#f59e0b" }, // amber
  { fill: "#fff7ed", border: "#fed7aa", text: "#9a3412", accent: "#f97316" }, // orange
  { fill: "#fff1f2", border: "#fecdd3", text: "#9f1239", accent: "#f43f5e" }, // rose
  { fill: "#fdf4ff", border: "#f5d0fe", text: "#86198f", accent: "#d946ef" }, // fuchsia
  { fill: "#faf5ff", border: "#e9d5ff", text: "#6b21a8", accent: "#a855f7" }, // purple
  { fill: "#eff6ff", border: "#bfdbfe", text: "#1e40af", accent: "#3b82f6" }, // blue
  { fill: "#f0fdfa", border: "#99f6e4", text: "#115e59", accent: "#14b8a6" }, // teal
  { fill: "#f8fafc", border: "#e2e8f0", text: "#334155", accent: "#64748b" }, // slate
];

/**
 * Stable subject → swatch mapping (FNV-1a over the subject code).
 * Pure and deterministic: same input, same swatch, forever.
 */
export function subjectColor(key) {
  const source = String(key || "");
  let hash = 2166136261;
  for (let i = 0; i < source.length; i += 1) {
    hash ^= source.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return SUBJECT_PALETTE[(hash >>> 0) % SUBJECT_PALETTE.length];
}

const teacherNames = (card) => (card.teachers || []).map((t) => t.name).join(", ");

/** What the card leads with, given which axis the current view already implies. */
function cardLines(card, view) {
  const teachers = teacherNames(card);
  if (view === "subject") {
    return { primary: card.group || "—", secondary: teachers, tag: card.subject_code };
  }
  if (view === "teacher") {
    return { primary: card.subject || "—", secondary: card.group || "", tag: card.group };
  }
  return { primary: card.subject || "—", secondary: teachers, tag: card.subject_code };
}

function BoardCard({
  card,
  view = "class",
  selected = false,
  busy = false,
  compact = false,
  // A committed / infeasible / cancelled run is history: still rendered, but
  // its cards cannot be moved or pinned.
  readOnly = false,
  // Grid placement, passed as primitives so React.memo actually holds.
  column = null,
  rowStart = null,
  lane = 0,
  lanes = 1,
  onSelect,
  onToggleLock,
}) {
  const { t } = useTranslation();
  const swatch = subjectColor(card.subject_code || card.subject || card.activity);
  const duration = Math.max(1, Number(card.duration) || 1);
  const { primary, secondary, tag } = cardLines(card, view);

  const style = useMemo(() => {
    const base = {
      backgroundColor: swatch.fill,
      borderColor: selected ? "#4f46e5" : swatch.border,
      color: swatch.text,
    };
    if (column == null || rowStart == null) return base;
    return {
      ...base,
      gridColumn: column,
      gridRow: `${rowStart} / span ${duration}`,
      // Several cards can legitimately share one slot in Teacher/Subject view
      // (different classes, same period). Lay them out in lanes rather than
      // stacking them invisibly on top of each other.
      ...(lanes > 1
        ? { width: `${100 / lanes}%`, left: `${(lane * 100) / lanes}%`, position: "relative" }
        : null),
    };
  }, [swatch, selected, column, rowStart, duration, lane, lanes]);

  // Most cards have NO room — a class that stays in its own room is never
  // room-constrained, so `room` is null for everything except the lessons that
  // contend for a lab / the gym. The chip is therefore additive: nothing about
  // the layout may depend on it being there.
  const roomName = card.room ? card.room.code || card.room.name : "";
  const spanLabel = duration > 1 ? t("timetable.spansPeriods", { count: duration }) : "";
  const roomLabel = roomName ? t("timetable.roomLabel", { room: roomName }) : "";
  const label = [primary, secondary, card.group, roomLabel, spanLabel]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      style={style}
      className={`group/card relative min-w-0 rounded-lg border overflow-hidden transition-shadow ${
        selected
          ? "ring-2 ring-indigo-500 ring-offset-1 shadow-lg z-30"
          : "hover:shadow-md z-20"
      } ${busy ? "opacity-60" : ""} ${compact ? "" : "h-full"}`}
    >
      {/* Subject accent rail — the colour cue survives even at 1-lane width. */}
      <span
        aria-hidden="true"
        className="absolute inset-y-0 left-0 w-1"
        style={{ backgroundColor: swatch.accent }}
      />

      {/* aSc-style corner stripe: a locked card survives regeneration. */}
      {card.is_locked && (
        <span
          aria-hidden="true"
          className="absolute top-0 right-0 w-0 h-0 border-t-[16px] border-l-[16px] border-l-transparent border-t-amber-500"
        />
      )}

      <button
        type="button"
        onClick={() => onSelect(card)}
        disabled={busy || readOnly}
        aria-pressed={selected}
        aria-label={label}
        className="w-full h-full text-left pl-2.5 pr-6 py-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-600 disabled:cursor-wait"
      >
        <p className="text-[11px] font-black leading-tight truncate">{primary}</p>
        {secondary && (
          <p className="text-[10px] font-semibold leading-tight truncate opacity-75 mt-0.5">
            {secondary}
          </p>
        )}
        <div className="flex items-center gap-1 mt-0.5 min-w-0">
          {tag && view !== "teacher" && (
            <span className="text-[9px] font-black uppercase tracking-wider opacity-50 truncate">
              {tag}
            </span>
          )}
          {roomName && (
            <span
              title={roomLabel}
              className="text-[9px] font-black uppercase tracking-wider px-1 rounded bg-white/70 shrink-0 max-w-[7ch] truncate"
            >
              {roomName}
            </span>
          )}
          {duration > 1 && (
            <span className="text-[9px] font-black uppercase tracking-wider px-1 rounded bg-white/70 shrink-0">
              ×{duration}
            </span>
          )}
        </div>
      </button>

      {!(readOnly && !card.is_locked) && (
      <button
        type="button"
        onClick={() => onToggleLock(card)}
        disabled={busy || readOnly}
        aria-pressed={Boolean(card.is_locked)}
        title={card.is_locked ? t("timetable.unlockCard") : t("timetable.lockCard")}
        aria-label={card.is_locked ? t("timetable.unlockCard") : t("timetable.lockCard")}
        className={`absolute bottom-0.5 right-0.5 p-1 rounded-md transition focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-indigo-600 disabled:cursor-wait ${
          card.is_locked
            ? "opacity-100 text-amber-700 bg-amber-100/80 hover:bg-amber-200"
            : "opacity-0 group-hover/card:opacity-100 text-slate-500 bg-white/80 hover:bg-white"
        }`}
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24" aria-hidden="true">
          {card.is_locked ? (
            <>
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path strokeLinecap="round" d="M8 11V7a4 4 0 118 0v4" />
            </>
          ) : (
            <>
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path strokeLinecap="round" d="M8 11V7a4 4 0 017.5-2" />
            </>
          )}
        </svg>
      </button>
      )}
    </div>
  );
}

// Every prop is a primitive or a state-stable object, so this shallow compare
// is exact: hovering, or selecting a card elsewhere, re-renders nothing here.
// That is what keeps an 800-card board responsive.
export default memo(BoardCard);
